import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { FileUploadsService } from '@app/services/file-uploads.service';
import { TurnoverBidsService } from '@app/services/turnover-bids.service';
import { RestTurnoverBidCategory } from '@shared/interfaces/turnover-bids.interface';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import {
  acceptTurnoverBid,
  acceptTurnoverBidFailed,
  acceptTurnoverBidSuccess,
  addTurnoverBidCategory,
  addTurnoverBidCategoryFailed,
  addTurnoverBidCategorySuccess,
  approveTurnoverBid,
  approveTurnoverBidFailed,
  approveTurnoverBidSuccess,
  createTurnoverBid,
  createTurnoverBidFailed,
  createTurnoverBidSuccess,
  loadTurnoverBidCategories,
  loadTurnoverBidCategoriesFailed,
  loadTurnoverBidCategoriesSuccess,
  loadTurnoverBids,
  loadTurnoverBidsFailed,
  loadTurnoverBidsSuccess,
  rejectTurnoverBid,
  rejectTurnoverBidFailed,
  rejectTurnoverBidSuccess,
  removeTurnoverBid,
  removeTurnoverBidFailed,
  removeTurnoverBidSuccess,
  updateTurnoverBid,
  updateTurnoverBidFailed,
  updateTurnoverBidSuccess,
  uploadTurnoverBidInvoice,
  uploadTurnoverBidInvoiceFailed,
  uploadTurnoverBidInvoiceSuccess,
} from '../actions/turnover-bids.actions';

@Injectable()
export class TurnoverBidsEffects {
  loadTurnoverBids$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTurnoverBids),
      mergeMap(action => {
        return this.turnoverBidsService.list(action.turnoverId).pipe(
          map(bids => loadTurnoverBidsSuccess({ bids })),
          catchError(() => of(loadTurnoverBidsFailed()))
        );
      })
    )
  );

  createTurnoverBid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createTurnoverBid),
      mergeMap(action => {
        return this.turnoverBidsService.add(action.bid).pipe(
          map(bidId => createTurnoverBidSuccess({ bidId, turnoverId: action.bid.turnoverId })),
          catchError(() => of(createTurnoverBidFailed()))
        );
      })
    )
  );

  createTurnoverBidSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createTurnoverBidSuccess),
      mergeMap(action => {
        return of(loadTurnoverBids({ turnoverId: action.turnoverId }));
      })
    )
  );

  acceptTurnoverBid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(acceptTurnoverBid),
      mergeMap(action => {
        return this.turnoverBidsService.accept(action.bidId).pipe(
          map(() => {
            this.snackbarService.success('Bid accepted');
            return acceptTurnoverBidSuccess();
          }),
          catchError(() => of(acceptTurnoverBidFailed()))
        );
      })
    )
  );

  rejectTurnoverBid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rejectTurnoverBid),
      mergeMap(action => {
        return this.turnoverBidsService.reject(action.bidId, action.reason).pipe(
          map(() => {
            this.snackbarService.success('Bid rejected');
            return rejectTurnoverBidSuccess();
          }),
          catchError(() => of(rejectTurnoverBidFailed()))
        );
      })
    )
  );

  uploadTurnoverBidInvoice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadTurnoverBidInvoice),
      mergeMap(action => {
        return this.fileUploadsService.uploadFile(action.file).pipe(
          map(upload => ({
            upload,
            bid: action.bid,
          }))
        );
      }),
      mergeMap(action => {
        return this.turnoverBidsService.uploadInvoice(action.bid.id, action.upload.id).pipe(
          mergeMap(() => [loadTurnoverBids({ turnoverId: action.bid.turnoverId }), uploadTurnoverBidInvoiceSuccess()]),
          catchError(() => of(uploadTurnoverBidInvoiceFailed()))
        );
      })
    )
  );

  updateTurnoverBid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateTurnoverBid),
      mergeMap(action => {
        return this.turnoverBidsService.update(action.bid).pipe(
          map(() => updateTurnoverBidSuccess({ bid: action.bid })),
          catchError(() => of(updateTurnoverBidFailed()))
        );
      })
    )
  );

  removeTurnoverBid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeTurnoverBid),
      mergeMap(action => {
        return this.turnoverBidsService.delete(action.bidId).pipe(
          map(() => removeTurnoverBidSuccess()),
          catchError(() => of(removeTurnoverBidFailed()))
        );
      })
    )
  );

  loadTurnoverBidCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTurnoverBidCategories),
      mergeMap(() => {
        return this.turnoverBidsService.categoryList().pipe(
          map((categories: RestTurnoverBidCategory[]) => loadTurnoverBidCategoriesSuccess({ categories })),
          catchError(() => of(loadTurnoverBidCategoriesFailed()))
        );
      })
    )
  );

  addTurnoverBidCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addTurnoverBidCategory),
      mergeMap(action => {
        return this.turnoverBidsService.addCategory(action.name).pipe(
          mergeMap((categoryId: number) => [
            loadTurnoverBidCategories(),
            addTurnoverBidCategorySuccess({ categoryId }),
          ]),
          catchError(() => of(addTurnoverBidCategoryFailed()))
        );
      })
    )
  );

  approveTurnoverBid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(approveTurnoverBid),
      mergeMap(action => {
        return this.turnoverBidsService.approve(action.bidId, action.approved, action.denyReason).pipe(
          map(() => {
            if (action.approved) {
              this.snackbarService.success('Bid approved');
            } else {
              this.snackbarService.success('Bid denied');
            }
            return approveTurnoverBidSuccess();
          }),
          catchError(() => of(approveTurnoverBidFailed()))
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private turnoverBidsService: TurnoverBidsService,
    private fileUploadsService: FileUploadsService,
    private snackbarService: SnackbarService
  ) {}
}
